<script setup lang="ts">
import TextButton from '@/components/input/Btn/TextButton.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import { useNotExportedOrderStore } from './useNotExportedOrderStore';
import { storeToRefs } from 'pinia';
import { onMounted } from 'vue';
import ExportOrderModal from '../components/exportModal/ExportOrderModal.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';

const notExportedOrderStore = useNotExportedOrderStore();
const { state } = storeToRefs(notExportedOrderStore);
const {
  init,
  onChangePaging,
  sortTableContentItems,
  onAppliedFilterModal,
  onClosedExportModal,
  updateMemo,
  deleteMemo,
  getFilterItemsValue,
  getFreeColumnFilterItemsValue,
} = notExportedOrderStore;
const t = i18n.t;
let renderingKey = 0; // 同期的にテーブルを再描画するためのkey

onMounted(async () => {
  await init();
  renderingKey += 1;
});
</script>

<template>
  <!-- 発注テーブルーページ -->
  <div class="not-exported-order">
    <!-- タイトル -->
    <div class="not-exported-order__title not-exported-order-title">
      <span class="not-exported-order-title__label">{{ t('未出力の発注') }}</span>
      <div class="not-exported-order-title__not-export-order not-export-order">
        <RouterLink class="not-export-order__label" to="/firm-order">{{ t('発注の進捗確認ページに切り替え') }}</RouterLink>
        <SvgLoader class="not-export-order__icon" name="orderSwitchIcon" />
      </div>
    </div>
    <div class="not-exported-order__main-contents not-exported-order-main-contents">
      <div class="not-exported-order-main-contents__top-accessory top-accessory">
        <div class="top-accessory__left">
          <!-- フィルターモーダル -->
          <FilterModalButton
            class="detail-filter-modal"
            :filter-modal-type="'order'"
            :select-input-index="0"
            :filter-items="getFilterItemsValue().value"
            :free-column-filter-items="getFreeColumnFilterItemsValue().value"
            @apply="onAppliedFilterModal"
          />
          <TextButton
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :on-click="
              () => {
                state.exportModalState = true;
              }
            "
            :disabled="state.checkedIds.length === 0"
            class="export-order-button"
          >
            {{ t('発注を出力') }}
          </TextButton>
        </div>
        <Pagination
          v-if="state.pagingOptions"
          :paging-options="state.pagingOptions"
          :is-loading="state.loadingState.status.type === 'loading'"
          is-order-page
          class="pagination"
          @on-change-page="onChangePaging($event)"
        />
      </div>
      <NewBaseTableContent
        :key="renderingKey"
        :prop-table-content-properties="state.tableContentProperties"
        :prop-display-objects="state.tableContentItems"
        :prop-loading-status="state.loadingState"
        :prop-table-type="'notExportedOrderTable'"
        class="not-exported-order-main-contents__table"
        @checked="state.checkedIds = $event"
        @on-click-sort="(sortKey) => sortTableContentItems(sortKey)"
        @on-click-save-memo="(orderId, memo) => updateMemo(orderId, memo)"
        @on-click-delete-memo="(orderId) => deleteMemo(orderId)"
      />
    </div>

    <!--  NOTE: FileInputを使用しない(S3テンプレート)を使用する場合   -->
    <!-- <ExportOrderModal
      :modal-state="state.exportModalState"
      @close-modal="state.exportModalState = false"
      @export-order="onClosedExportModal()"
    /> -->
    <ExportOrderModal
      :modal-state="state.exportModalState"
      @close-modal="state.exportModalState = false"
      @export-order="onClosedExportModal($event)"
    />
    <!-- メモ更新モーダル -->
    <InfoModal :modal-state="isLoading(state.updateMemoState.loading.status)" icon="loading" :enable-outside-click="false">
      <template #title>{{ state.updateMemoState.workType === 'delete' ? t('メモを削除しています') : t('メモを保存しています') }}</template>
    </InfoModal>
  </div>
</template>

<style lang="scss" scoped>
.not-exported-order {
  display: grid;
  gap: 16px;
  margin: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
  }

  &__main-contents {
    display: grid;
    &--loading {
      align-self: center;
      height: 100%;
    }
  }
}

.not-exported-order-title {
  display: grid;
  grid-template-columns: repeat(2, fit-content(100%));
  align-items: center;
  column-gap: 48px;
  &__label {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
  }
}

.not-export-order {
  display: grid;
  height: 100%;
  grid-template-columns: auto auto;
  align-items: baseline;
  justify-items: center;
  column-gap: 4px;
  pointer-events: none;
  cursor: pointer;
  padding: 0px 12px 4px 12px;

  &:hover {
    background: $semanticPrimarySubdued;
    transition: 0.3ms;
    border-radius: 90px;
  }
  &__label {
    pointer-events: auto;
    font-size: 16px;
    font-weight: 700;
    line-height: 16px;
    color: $semanticPrimary;
  }
  &__icon {
    pointer-events: auto;
    width: 20px;
    height: 20px;
    align-content: end;
  }
}

.export-order-button {
  width: fit-content;
  height: 42px;
}

.not-exported-order-main-contents {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 8px;
  &__top-accessory {
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
  }

  &__table {
    display: grid;
    &--loading {
      align-self: center;
      height: 100%;
    }
  }
}

.top-accessory {
  &__left {
    display: grid;
    grid-auto-flow: column;
    column-gap: 24px;
    align-items: center;
  }
}
</style>
